/**
 * Session utilisateur — obtention et conservation du jeton d'accès.
 *
 * Le jeton est conservé dans `sessionStorage` (et non `localStorage`) : il survit au
 * rechargement et à la navigation par URL directe — sans quoi tout rafraîchissement
 * déconnectait l'utilisateur, défaut d'utilisabilité majeur (U2). Mais il est effacé à
 * la fermeture de l'onglet et n'est pas partagé entre onglets, ce qui limite la fenêtre
 * d'exposition en cas de XSS. Durcissement ultérieur possible : refresh-token silencieux
 * (le client Keycloak est déjà configuré pour le code d'autorisation + PKCE).
 */

import { definirLangueCourante, langueChoisie, langueCourante, normaliserLocale } from '@/i18n/language'

const TOKEN_PATH = '/realms/specfuse/protocol/openid-connect/token'
const AUTHORIZE_PATH = '/realms/specfuse/protocol/openid-connect/auth'
const END_SESSION_PATH = '/realms/specfuse/protocol/openid-connect/logout'
const BFF_AUTH_PATH = '/api/v1/auth'
const STORAGE_KEY = 'doit-session'
const OIDC_TRANSACTION_KEY = 'doit-oidc-transaction'

interface OidcTransaction {
  state: string
  verifier: string
  redirectUri: string
}

/**
 * Construit l'endpoint public du realm sans exposer le port interne de Keycloak.
 * En l'absence de surcharge, Vite et nginx relaient `/auth` vers Keycloak.
 */
export function keycloakTokenUrl(
  base = import.meta.env['VITE_KEYCLOAK_URL'] ?? '/auth',
): string {
  return `${base}${TOKEN_PATH}`
}

/** L'endpoint d'autorisation est distinct de celui des jetons : le navigateur est
 * redirigé vers Keycloak, donc le mot de passe ne traverse jamais l'interface DoIt. */
export function keycloakAuthorizationUrl(
  base = import.meta.env['VITE_KEYCLOAK_URL'] ?? '/auth',
): string {
  return `${base}${AUTHORIZE_PATH}`
}

/** RP-Initiated Logout (OIDC standard) : termine aussi la session SSO Keycloak, pas
 * seulement l'état local DoIt (v030-login-redirection-directe-keycloak-theme). */
export function keycloakEndSessionUrl(
  base = import.meta.env['VITE_KEYCLOAK_URL'] ?? '/auth',
): string {
  return `${base}${END_SESSION_PATH}`
}

/** Le formulaire intégré ne connaît jamais le secret du client Keycloak : il parle
 * uniquement à la passerelle same-origin, qui porte le client confidentiel dédié. */
export function gatewayLoginUrl(): string { return `${BFF_AUTH_PATH}/login` }
export function gatewayRefreshUrl(): string { return `${BFF_AUTH_PATH}/refresh` }

/** Locale unique passée aux deux conventions Keycloak depuis la préférence SPA. */
export function localeKeycloakDemandee(): 'fr' | 'en' {
  return normaliserLocale(langueCourante())
}

export interface Session {
  token: string
  username: string
  /** Identifiant technique de l'utilisateur (claim `user_id`) — celui que le serveur
   *  inscrit dans `requested_by`/`decided_by`. Sert à détecter « je suis le demandeur »
   *  pour expliquer la SoD en amont (AUD-UX-05). */
  userId?: string
  organisationId?: string
  /** Rôles du realm (`workspace_owner|contributor|viewer`) décodés du jeton. */
  roles: string[]
  expiresAt: number
  /** Jeton de rafraîchissement : permet de renouveler l'accès sans re-saisir le mot de
   *  passe, pour que la session ne tombe pas au bout de 5 min (AUD-UX-03). */
  refreshToken?: string
  refreshExpiresAt?: number
  /** Jeton d'identité OIDC brut : requis par `logout()` pour terminer la session Keycloak
   *  (`id_token_hint`) et non seulement l'état local. */
  idToken?: string
}

interface TokenPayload {
  access_token?: string
  expires_in?: number
  refresh_token?: string
  refresh_expires_in?: number
  id_token?: string
}

/** Un lecteur ne peut qu'observer : toute action d'écriture lui est refusée côté UI
 *  (et côté serveur). Propriétaire et contributeur écrivent. */
export function canWrite(session: Session | null): boolean {
  if (!session) return false
  return session.roles.includes('workspace_owner')
      || session.roles.includes('workspace_contributor')
}

/**
 * FND-V050-083 (rejeu caisse RJ-04) : le serveur n'accepte AUCUNE écriture sous `/api/v1/**`
 * sans rôle de realm propriétaire ou contributeur (`SecurityConfig.ROLES_ECRITURE`), quelle que
 * soit l'appartenance au projet. L'administrateur de plateforme (`admin` seul) se voyait
 * proposer « Créer mon premier espace », « Valider cette version », « Approuver » — refusés en
 * 403. Motif du refus AVANT le clic, ou `null` si le jeton autorise l'écriture.
 */
export function motifEcritureRefusee(session: Session | null): string | null {
  if (!session || canWrite(session)) return null
  if (session.roles.includes('admin')) {
    return 'Administrateur de plateforme : ce compte administre la plateforme mais n’écrit pas dans les projets '
      + '(créer un projet, valider, approuver). Connectez-vous avec un compte propriétaire ou contributeur.'
  }
  return 'Lecture seule : votre rôle ne permet pas cette action. Un propriétaire ou un contributeur peut la faire.'
}

/** Rôles effectifs plafonnés par le JETON : l'appartenance au projet n'ouvre pas l'écriture
 *  que la règle serveur refuse (FND-V050-083). */
export function plafonnerAuJeton(rolesJeton: string[], rolesEffectifs: string[]): string[] {
  const jetonEcrit = rolesJeton.includes('workspace_owner') || rolesJeton.includes('workspace_contributor')
  if (jetonEcrit) return rolesEffectifs
  const ecriture = rolesEffectifs.includes('workspace_owner') || rolesEffectifs.includes('workspace_contributor')
  const restants = rolesEffectifs.filter((r) => r !== 'workspace_owner' && r !== 'workspace_contributor')
  return ecriture && !restants.includes('workspace_viewer') ? ['workspace_viewer', ...restants] : restants
}

/** Pastille « Lecture seule » (FND-V050-079) : l'administrateur de plateforme n'écrit pas
 *  dans le contenu d'un espace (`canWrite` suit `ROLES_ECRITURE` du serveur), mais il n'est
 *  pas en lecture seule — organisations, plateforme IA, opérations lui sont ouvertes en
 *  écriture (`SecurityConfig`, `hasRole("ADMIN")`). Lui afficher « Lecture seule » au
 *  tableau de bord vierge décrivait mal ses droits. */
export function estLectureSeule(session: Session | null): boolean {
  if (!session) return false
  return !canWrite(session) && !session.roles.includes('admin')
}

/** Seul le propriétaire de l'espace ou l'administrateur global peut ouvrir le
 * cockpit d'administration, conformément aux routes serveur `/admin/**`. Les
 * actions de périmètre restent protégées par leurs contrôles dédiés. Lot USR : l'administrateur
 * d'organisation (`org_admin`, qui porte aussi `workspace_owner`) y entre ; l'auditeur NON — les
 * réglages d'un projet sont des écritures, il lit la matrice et le journal par leurs propres écrans. */
export function canAccessAdministration(session: Session | null): boolean {
  return Boolean(session && (session.roles.includes('workspace_owner') || session.roles.includes('admin')
    || session.roles.includes('org_admin')))
}

/** Lot USR (ARB-SEC-USR-01) : administrateur de l'organisation (rôle de realm `org_admin`). */
export function estAdministrateurOrganisation(session: Session | null): boolean {
  return Boolean(session?.roles.includes('org_admin'))
}

/**
 * v05x-secu-acces (M8) : ADMINISTRER L'ORGANISATION — connexions et modèles d'IA, arrêt d'urgence de
 * l'organisation, registre MCP, purge de rétention : administrateur d'organisation (`org_admin`) ou de
 * la plateforme (`admin`). Plus un simple propriétaire de projet : la passerelle le refuse
 * (`ORG_ADMIN_REQUIRED`). Lire les rôles du JETON (`useSession`) : `org_admin` est un rôle
 * d'organisation, il ne dépend pas du projet ouvert.
 */
export function peutAdministrerOrganisation(session: Session | null): boolean {
  return Boolean(session && (session.roles.includes('org_admin') || session.roles.includes('admin')))
}

/**
 * v05x-secu-acces (M8) : régler un PROJET sous « Réglages du projet » (`/admin/workspace`, quotas, modèles
 * autorisés, webhooks, conformité) — propriétaire de CE projet (`membership_role`), administrateur
 * d'organisation ou de la plateforme. Le rôle de realm `workspace_owner` ne suffit plus : il est porté
 * par le propriétaire de N'IMPORTE QUEL projet (`PROJECT_OWNER_REQUIRED` côté passerelle).
 */
export function peutAdministrerProjet(session: Session | null, roleDansProjet: string | null | undefined): boolean {
  return Boolean(session && (peutAdministrerOrganisation(session) || roleDansProjet === 'owner'))
}

/**
 * LIRE le catalogue des objectifs de service d'un projet (`GET /admin/slo-catalog`) : la passerelle le juge comme
 * l'écrire (`AdminSloController#list` appelle `ProjectWriteGuard#assertCanAdministerProjectOrOrganisation`), par
 * APPARTENANCE — le propriétaire de CE projet (`membership_role`), l'administration de l'organisation ou celle de la
 * plateforme. Le rôle de realm du jeton n'y entre pas : le propriétaire par appartenance dont le jeton est
 * contributeur lit son catalogue, et le porteur de `workspace_owner` d'un AUTRE projet est refusé (403
 * `PROJECT_OWNER_REQUIRED`, consigné). Même prédicat que `peutAdministrerProjet`, sous le nom du geste qu'il garde.
 * À appeler avec les rôles du JETON (`useSession`) — `org_admin` et `admin` ne dépendent pas du projet ouvert — et le rôle
 * de la personne dans le projet ouvert.
 */
export function peutLireCatalogueSlo(session: Session | null, roleDansProjet: string | null | undefined): boolean {
  return peutAdministrerProjet(session, roleDansProjet)
}

/**
 * v05x-secu-acces (trou n° 2) : exporter l'audit d'un projet (`GET /audit/export`) — son propriétaire,
 * l'auditeur de l'organisation, son administrateur ou la plateforme (`AUDIT_EXPORT_ROLE_REQUIRED`).
 */
export function peutExporterAuditProjet(session: Session | null, roleDansProjet: string | null | undefined): boolean {
  return Boolean(session && (peutAdministrerProjet(session, roleDansProjet) || session.roles.includes('auditor')))
}

/** Lot USR (ARB-SEC-USR-05) : auditeur en lecture seule (rôle de realm `auditor`). */
export function estAuditeur(session: Session | null): boolean {
  return Boolean(session?.roles.includes('auditor'))
}

/**
 * Lot USR : lire l'annuaire, les invitations, la politique de connexion, la matrice des droits et
 * le journal de TOUTE l'organisation — administrateur d'organisation, auditeur, plateforme.
 */
export function peutLireAdministrationOrganisation(session: Session | null): boolean {
  return Boolean(session && (estAdministrateurOrganisation(session) || estAuditeur(session)
    || session.roles.includes('admin')))
}

const MOTIF_DECONNEXION_KEY = 'doit-motif-deconnexion'

/** Lot USR : pourquoi la passerelle a mis fin à la session (durée maximale, droits changés). */
export function noterMotifDeconnexion(code: string): void {
  try { sessionStorage.setItem(MOTIF_DECONNEXION_KEY, code) } catch { /* stockage indisponible */ }
}

/** Lit puis efface le motif (affiché une seule fois par l'écran de connexion). */
export function lireMotifDeconnexion(): string | null {
  try {
    const motif = sessionStorage.getItem(MOTIF_DECONNEXION_KEY)
    if (motif) sessionStorage.removeItem(MOTIF_DECONNEXION_KEY)
    return motif
  } catch {
    return null
  }
}

/** La configuration de gouvernance engage toute l'organisation : owner uniquement. */
export function canManageConfiguration(session: Session | null): boolean {
  return Boolean(session?.roles.includes('workspace_owner'))
}

/**
 * Le rôle principal de la session : clé i18n et libellé français par défaut. v05x-org-neuve (parcours
 * réel du 2026-10-05) : le menu utilisateur traduisait le libellé par une table qui ignorait
 * l'administration d'organisation et l'audit — « Rôle inconnu » s'affichait pour une
 * administratrice d'organisation. Une seule source désormais : la clé suit le rôle.
 */
export function roleSession(session: Session | null): { cle: string; libelle: string } {
  if (!session) return { cle: '', libelle: '' }
  if (session.roles.includes('org_admin')) return { cle: 'role.orgAdmin', libelle: 'Administratrice/Administrateur d’organisation' }
  if (session.roles.includes('auditor')) return { cle: 'role.auditor', libelle: 'Auditrice/Auditeur' }
  if (session.roles.includes('workspace_owner')) return { cle: 'role.owner', libelle: 'Propriétaire' }
  if (session.roles.includes('workspace_contributor')) return { cle: 'role.contributor', libelle: 'Contributeur' }
  if (session.roles.includes('workspace_viewer')) return { cle: 'role.viewer', libelle: 'Lecteur' }
  if (session.roles.includes('admin')) return { cle: 'role.admin', libelle: 'Administrateur de plateforme' }
  return { cle: 'role.unknown', libelle: 'Rôle inconnu' }
}

/** Un libellé humain (français) du rôle principal, pour l'afficher sans jargon technique. */
export function roleLabel(session: Session | null): string {
  return roleSession(session).libelle
}

function persist(session: Session | null): void {
  try {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* stockage indisponible : la session reste valable en mémoire pour l'onglet */
  }
}

function restore(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Session
    if (!parsed.token) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    // Accès encore valide : session restaurée telle quelle.
    if (parsed.expiresAt > Date.now()) return parsed
    // Accès expiré MAIS jeton de rafraîchissement encore valide : on garde la session,
    // elle sera renouvelée aussitôt (au lieu de déconnecter au rechargement).
    if (parsed.refreshToken && (parsed.refreshExpiresAt ?? 0) > Date.now()) return parsed
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  } catch {
    return null
  }
}

let current: Session | null = restore()
const listeners = new Set<(s: Session | null) => void>()
let minuteurRafraichissement: ReturnType<typeof setTimeout> | null = null
/** v030-login-redirection-directe-keycloak-theme (r2, course trouvée en direct) :
 *  `logout()` navigue vers Keycloak, mais `notify()` (appelé juste avant) démonte
 *  synchronement l'écran courant et remonte `<Login/>`, dont l'effet lance SA PROPRE
 *  redirection (`beginLogin()`) — une seconde navigation `window.location.assign()`
 *  qui peut GAGNER la course contre celle de `logout()` (mesuré : `openid-connect/
 *  logout` annulé en vol, `-1`, jamais reçu par Keycloak ; le realm reste donc en
 *  session SSO active, et l'authorize de la seconde navigation la réutilise en
 *  silence, avec un jeton PKCE qui ne correspond plus à la bonne transaction —
 *  `invalid_code` côté Keycloak). Ce drapeau laisse Login.tsx détecter qu'une
 *  redirection de déconnexion est DÉJÀ en cours et s'abstenir de la court-circuiter. */
let redirectionDeconnexionEnCours = false

export function estEnRedirectionDeconnexion(): boolean {
  return redirectionDeconnexionEnCours
}

function notify(): void {
  for (const listener of listeners) listener(current)
}

export function subscribe(listener: (s: Session | null) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getSession(): Session | null {
  if (current && current.expiresAt <= Date.now()) {
    // Accès expiré : si un jeton de rafraîchissement valide existe, on renouvelle
    // (asynchrone) et on GARDE la session — plutôt qu'une déconnexion brutale. Sinon,
    // on efface : mieux vaut une reconnexion explicite qu'une cascade de 401.
    if (current.refreshToken && (current.refreshExpiresAt ?? 0) > Date.now()) {
      void rafraichir()
      return current
    }
    current = null
    persist(null)
  }
  return current
}

export function getToken(): string | null {
  return getSession()?.token ?? null
}

function decodeClaims(token: string): Record<string, unknown> {
  const part = token.split('.')[1]
  if (!part) return {}
  const padded = part.padEnd(part.length + ((4 - (part.length % 4)) % 4), '=')
  try {
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>
  } catch {
    return {}
  }
}

function construireSession(payload: TokenPayload, repliUsername: string): Session {
  const claims = decodeClaims(payload.access_token ?? '')
  const realmAccess = claims['realm_access']
  const roles = (realmAccess && typeof realmAccess === 'object'
    && Array.isArray((realmAccess as { roles?: unknown }).roles))
    ? ((realmAccess as { roles: unknown[] }).roles.filter(
        (r): r is string => typeof r === 'string'))
    : []
  return {
    token: payload.access_token as string,
    username: String(claims['preferred_username'] ?? repliUsername),
    ...(typeof claims['user_id'] === 'string' && claims['user_id'].trim().length > 0
      ? { userId: claims['user_id'] }
      : typeof claims.sub === 'string' && claims.sub.trim().length > 0
        ? { userId: claims.sub } : {}),
    ...(typeof claims['organisation_id'] === 'string'
      ? { organisationId: claims['organisation_id'] }
      : {}),
    roles,
    expiresAt: Date.now() + (payload.expires_in ?? 300) * 1000,
    ...(payload.refresh_token
      ? {
          refreshToken: payload.refresh_token,
          refreshExpiresAt: Date.now() + (payload.refresh_expires_in ?? 1800) * 1000,
        }
      : {}),
    ...(payload.id_token ? { idToken: payload.id_token } : {}),
  }
}

/**
 * Le claim `locale` est émis par le mapper du client Keycloak. Après le callback,
 * la préférence de profil Keycloak devient l'autorité de la SPA ; une valeur
 * inattendue se rabat explicitement sur `fr`, jamais sur un mélange de libellés.
 */
function synchroniserLocaleKeycloak(payload: TokenPayload): void {
  // RJ-08 : un choix explicite fait dans l'interface prime sur la locale du profil Keycloak.
  const choisie = langueChoisie()
  if (choisie) {
    definirLangueCourante(choisie)
    return
  }
  const locale = decodeClaims(payload.access_token ?? '')['locale']
  definirLangueCourante(normaliserLocale(locale))
}

/** Programme un rafraîchissement 30 s avant l'expiration de l'accès : tant que l'onglet
 *  est ouvert, la session ne tombe jamais en cours d'usage. */
function programmerRafraichissement(): void {
  if (minuteurRafraichissement) {
    clearTimeout(minuteurRafraichissement)
    minuteurRafraichissement = null
  }
  if (!current?.refreshToken) return
  const delai = Math.max(2000, current.expiresAt - Date.now() - 30_000)
  minuteurRafraichissement = setTimeout(() => {
    void rafraichir()
  }, delai)
}

/** Renouvelle le jeton d'accès via le jeton de rafraîchissement. Sur échec, déconnecte. */
export async function rafraichir(): Promise<Session | null> {
  const jeton = current?.refreshToken
  if (!jeton || (current?.refreshExpiresAt ?? 0) <= Date.now()) {
    logout()
    return null
  }
  let response: Response
  try {
    response = await fetch(gatewayRefreshUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: jeton }),
    })
  } catch {
    // Réseau momentanément indisponible : on garde la session, on réessaiera bientôt.
    if (current) programmerRafraichissement()
    return current
  }
  if (!response.ok) {
    logout()
    return null
  }
  const payload = (await response.json()) as TokenPayload
  if (!payload.access_token) {
    logout()
    return null
  }
  current = construireSession(payload, current?.username ?? '')
  synchroniserLocaleKeycloak(payload)
  persist(current)
  notify()
  programmerRafraichissement()
  return current
}

// Au chargement du module (rechargement de page) : si une session valide est restaurée,
// on programme son rafraîchissement — et si l'accès était expiré mais le refresh valide,
// le délai minimal (2 s) déclenche un renouvellement quasi immédiat.
if (current) programmerRafraichissement()

export class LoginError extends Error {}

/** Refus temporaire, distinct d'un vrai 401 mais sans détail sur l'identité demandée. */
export class LoginRateLimitError extends LoginError {
  constructor(readonly retryAfterSeconds: number) {
    super('Trop de tentatives')
  }
}

function erreurConnexion(response: Response): LoginError {
  if (response.status === 429) {
    const parsed = Number(response.headers.get('Retry-After'))
    // La passerelle fournit toujours cette information. Le repli garde toutefois une
    // consigne actionnable si un proxy intermédiaire la supprimait.
    const retryAfterSeconds = Number.isFinite(parsed) && parsed > 0 ? Math.ceil(parsed) : 60
    return new LoginRateLimitError(retryAfterSeconds)
  }
  // FND-V050-086 : un 5xx vient du proxy ou de la passerelle (nginx 502 « api-gateway could
  // not be resolved »), jamais d'un refus d'identité : la passerelle rend 401 pour un refus,
  // y compris quand Keycloak est indisponible. Nommer le service ne révèle rien du compte.
  if (response.status >= 500) return new LoginError("Service d'authentification injoignable")
  // La passerelle ne révèle ni la cause Keycloak ni l'existence du compte.
  return new LoginError('Identifiants invalides')
}

/** Ouvre une session avec le BFF. Le mot de passe reste dans la portée de cet appel :
 * ni sessionStorage, ni état applicatif, ni URL ne le conservent. */
export async function loginWithPassword(username: string, password: string): Promise<Session> {
  let response: Response
  try {
    response = await fetch(gatewayLoginUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
  } catch {
    throw new LoginError("Service d'authentification injoignable")
  }
  if (!response.ok) {
    throw erreurConnexion(response)
  }
  const payload = (await response.json()) as TokenPayload
  if (!payload.access_token) throw new LoginError("Aucun jeton n'a été délivré")
  current = construireSession(payload, username)
  synchroniserLocaleKeycloak(payload)
  persist(current)
  notify()
  programmerRafraichissement()
  return current
}

function base64Url(bytes: Uint8Array): string {
  let value = ''
  for (const byte of bytes) value += String.fromCharCode(byte)
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function randomValue(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

async function sha256Base64Url(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value)
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
}

/** Démarre Authorization Code + PKCE. Le verifier reste seulement dans sessionStorage,
 * lié au state; le callback ne peut donc ni ouvrir une session forgée ni réutiliser un
 * code intercepté sans le secret éphémère du navigateur initiateur. */
export async function beginLogin(): Promise<void> {
  const verifier = randomValue()
  const state = randomValue()
  const redirectUri = window.location.origin + window.location.pathname
  const challenge = await sha256Base64Url(verifier)
  const transaction: OidcTransaction = { state, verifier, redirectUri }
  sessionStorage.setItem(OIDC_TRANSACTION_KEY, JSON.stringify(transaction))
  const locale = localeKeycloakDemandee()
  const params = new URLSearchParams({
    client_id: 'doit-ui', response_type: 'code', redirect_uri: redirectUri,
    scope: 'openid profile email', state, code_challenge: challenge,
    code_challenge_method: 'S256',
    // Les deux noms sont compris par Keycloak : `ui_locales` est OIDC standard,
    // `kc_locale` rend la préférence aussi explicite pour le thème Keycloak.
    ui_locales: locale, kc_locale: locale,
  })
  window.location.assign(`${keycloakAuthorizationUrl()}?${params.toString()}`)
}

/** Consomme exactement une fois le callback OIDC courant. L'absence ou la divergence du
 * state est un refus explicite; aucun appel token n'est alors envoyé. */
export async function completeLoginRedirect(url = window.location.href): Promise<Session | null> {
  const callback = new URL(url)
  const code = callback.searchParams.get('code')
  const state = callback.searchParams.get('state')
  if (!code && !state && !callback.searchParams.get('error')) return null
  const raw = sessionStorage.getItem(OIDC_TRANSACTION_KEY)
  sessionStorage.removeItem(OIDC_TRANSACTION_KEY)
  if (!raw) throw new LoginError('Retour de connexion invalide ou expiré')
  let transaction: OidcTransaction
  try { transaction = JSON.parse(raw) as OidcTransaction } catch {
    throw new LoginError('Retour de connexion invalide ou expiré')
  }
  if (!code || !state || state !== transaction.state || callback.searchParams.get('error')) {
    throw new LoginError('Connexion refusée ou retour de connexion invalide')
  }
  let response: Response
  try {
    response = await fetch(keycloakTokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: 'doit-ui',
        grant_type: 'authorization_code',
        code,
        redirect_uri: transaction.redirectUri,
        code_verifier: transaction.verifier,
      }),
    })
  } catch (cause) {
    throw new LoginError(`Service d'authentification injoignable (${String(cause)})`)
  }

  if (!response.ok) {
    throw erreurConnexion(response)
  }

  const payload = (await response.json()) as TokenPayload
  if (!payload.access_token) throw new LoginError("Aucun jeton n'a été délivré")

  current = construireSession(payload, '')
  synchroniserLocaleKeycloak(payload)
  persist(current)
  notify()
  programmerRafraichissement()
  return current
}

/** Termine la session localement ET côté Keycloak (RP-Initiated Logout, OIDC standard).
 *
 * v030-login-redirection-directe-keycloak-theme : un nettoyage purement local suffisait
 * tant que revenir sur l'écran de connexion exigeait un clic explicite. Avec la
 * redirection automatique, un nettoyage local-seul reconnecterait SILENCIEUSEMENT
 * l'utilisateur via la session SSO Keycloak encore valide — « Se déconnecter » deviendrait
 * un mensonge sur un poste partagé (défaut de sécurité réel, pas seulement un défaut de
 * test). On ne redirige vers Keycloak que s'il y avait réellement une session à terminer
 * (jamais pour un appel défensif sans session active).
 */
export function logout(): void {
  const idToken = current?.idToken
  const avaitUneSession = current !== null
  // Posé AVANT `notify()` : Login.tsx (qui va monter en réaction à ce même `notify()`)
  // doit voir ce drapeau dès son tout premier rendu, jamais après.
  if (avaitUneSession) redirectionDeconnexionEnCours = true
  if (minuteurRafraichissement) {
    clearTimeout(minuteurRafraichissement)
    minuteurRafraichissement = null
  }
  current = null
  persist(null)
  notify()
  if (!avaitUneSession) return
  const postLogoutRedirectUri = window.location.origin + window.location.pathname
  const params = new URLSearchParams({ post_logout_redirect_uri: postLogoutRedirectUri })
  if (idToken) params.set('id_token_hint', idToken)
  else params.set('client_id', 'doit-ui')
  window.location.assign(`${keycloakEndSessionUrl()}?${params.toString()}`)
}
